import React, { useEffect, useMemo, useRef, useState } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import { Volume2 } from "lucide-react";
import { axiosInstance } from "@/App";

export const LIVE_EVENT_PREVIEW_SECONDS = 10;

export const liveEventPreviewStorageKey = (eventId, startedAt, viewerIdentity = "anonymous") => (
  `showmelive_live_preview_seen:v2:${viewerIdentity}:${eventId}:${startedAt || "live"}`
);

export const hasSeenLiveEventPreview = (storageKey) => {
  try {
    return window.localStorage.getItem(storageKey) === "1";
  } catch (_) {
    return false;
  }
};

export const markLiveEventPreviewSeen = (storageKey) => {
  try {
    window.localStorage.setItem(storageKey, "1");
  } catch (_) {}
};

const IS_IOS_WEBKIT = (() => {
  const userAgent = window.navigator?.userAgent || "";
  const platform = window.navigator?.platform || "";
  const touchMac = platform === "MacIntel" && window.navigator?.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(userAgent) || touchMac;
})();

const LiveEventPreview = ({
  eventId,
  startedAt,
  imageUrl,
  title,
  viewerIdentity,
  variant = "card",
  onPreviewActiveChange,
}) => {
  const storageKey = useMemo(
    () => liveEventPreviewStorageKey(eventId, startedAt, viewerIdentity || "anonymous"),
    [eventId, startedAt, viewerIdentity],
  );
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const playbackRef = useRef(null);
  const [phase, setPhase] = useState(() => (
    hasSeenLiveEventPreview(storageKey) ? "complete" : "connecting"
  ));
  const [secondsRemaining, setSecondsRemaining] = useState(LIVE_EVENT_PREVIEW_SECONDS);

  useEffect(() => {
    if (!eventId || hasSeenLiveEventPreview(storageKey)) {
      setPhase("complete");
      return undefined;
    }

    let active = true;
    let room = null;
    let attachedVideoTrack = null;
    let attachedAudioTrack = null;
    let videoIsPlaying = false;
    let audioIsPlaying = false;
    let previewStarted = false;
    let finishTimer = null;
    let countdownTimer = null;
    let connectionTimer = null;
    let previewSeconds = LIVE_EVENT_PREVIEW_SECONDS;
    let activeParticipantIdentity = null;

    const stopRoom = () => {
      window.clearTimeout(finishTimer);
      window.clearTimeout(connectionTimer);
      window.clearInterval(countdownTimer);
      if (attachedVideoTrack && videoRef.current) attachedVideoTrack.detach(videoRef.current);
      if (attachedAudioTrack && audioRef.current) attachedAudioTrack.detach(audioRef.current);
      attachedVideoTrack = null;
      attachedAudioTrack = null;
      room?.disconnect();
      room = null;
    };

    const finishPreview = () => {
      if (!active) return;
      stopRoom();
      setSecondsRemaining(0);
      setPhase("complete");
    };

    const beginPreview = () => {
      if (!active || previewStarted) return;
      previewStarted = true;
      markLiveEventPreviewSeen(storageKey);
      if (viewerIdentity) {
        axiosInstance.post(`/livekit/preview/${eventId}/viewed`).catch(() => {});
      }
      setPhase("playing");
      setSecondsRemaining(previewSeconds);
      const deadline = Date.now() + previewSeconds * 1000;
      countdownTimer = window.setInterval(() => {
        if (!active) return;
        setSecondsRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
      }, 250);
      finishTimer = window.setTimeout(finishPreview, previewSeconds * 1000);
    };
    const maybeBeginPreview = () => {
      if (videoIsPlaying && audioIsPlaying) beginPreview();
    };

    playbackRef.current = async () => {
      const video = videoRef.current;
      const audio = audioRef.current;
      if (!video || !audio || !attachedVideoTrack || !attachedAudioTrack) return;
      try {
        await room?.startAudio?.();
        await Promise.all([video.play(), audio.play()]);
      } catch (_) {
        if (active) setPhase("awaiting-gesture");
      }
    };

    const handleTrackSubscribed = (track, _publication, participant) => {
      if (!active) return;
      if (activeParticipantIdentity && participant?.identity !== activeParticipantIdentity) return;

      if (track.kind === Track.Kind.Video && !attachedVideoTrack) {
        const video = videoRef.current;
        if (!video) return;
        attachedVideoTrack = track;
        track.attach(video);
        video.addEventListener("playing", () => {
          videoIsPlaying = true;
          maybeBeginPreview();
        }, { once: true });
      }

      if (track.kind === Track.Kind.Audio && !attachedAudioTrack) {
        const audio = audioRef.current;
        if (!audio) return;
        attachedAudioTrack = track;
        track.attach(audio);
        audio.muted = false;
        audio.volume = 1;
        audio.addEventListener("playing", () => {
          audioIsPlaying = true;
          maybeBeginPreview();
        }, { once: true });
      }

      if (attachedVideoTrack && attachedAudioTrack) playbackRef.current?.();
    };

    const connectPreview = async () => {
      try {
        setPhase("connecting");
        const response = await axiosInstance.post(`/livekit/preview/${eventId}`);
        if (!active) return;
        const credentials = response.data || {};
        previewSeconds = Math.min(
          LIVE_EVENT_PREVIEW_SECONDS,
          Math.max(1, Number(credentials.preview_seconds) || LIVE_EVENT_PREVIEW_SECONDS),
        );
        activeParticipantIdentity = credentials.active_participant_identity || null;
        room = new Room({ adaptiveStream: true, dynacast: true });
        room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        connectionTimer = window.setTimeout(() => {
          if (!previewStarted && active) {
            stopRoom();
            setPhase("unavailable");
          }
        }, 12_000);
        await room.connect(credentials.url, credentials.token, {
          autoSubscribe: true,
          rtcConfig: IS_IOS_WEBKIT ? { iceTransportPolicy: "relay" } : undefined,
        });
      } catch (_) {
        if (active) {
          stopRoom();
          setPhase("unavailable");
        }
      }
    };

    connectPreview();
    return () => {
      active = false;
      playbackRef.current = null;
      stopRoom();
    };
  }, [eventId, storageKey, viewerIdentity]);

  useEffect(() => {
    const active = phase === "connecting" || phase === "playing" || phase === "awaiting-gesture";
    onPreviewActiveChange?.(active);
    return () => onPreviewActiveChange?.(false);
  }, [onPreviewActiveChange, phase]);

  const previewVisible = phase === "playing" || phase === "awaiting-gesture";
  const isHero = variant === "hero";

  return (
    <div
      className={isHero
        ? "absolute inset-0 h-full w-full overflow-hidden bg-black"
        : "relative mx-auto h-80 w-full max-w-sm overflow-hidden rounded-lg bg-black shadow-2xl"}
      data-testid="live-event-preview"
      data-preview-phase={phase}
    >
      <img
        src={imageUrl}
        alt={title}
        className={`absolute inset-0 h-full w-full opacity-80 ${isHero ? "object-cover" : "object-contain"}`}
        draggable="false"
        onContextMenu={(event) => event.preventDefault()}
      />
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        disablePictureInPicture
        controlsList="nodownload noplaybackrate noremoteplayback"
        className={`absolute inset-0 h-full w-full bg-black transition-opacity duration-300 ${isHero ? "object-cover" : "object-contain"} ${
          previewVisible ? "opacity-100" : "opacity-0"
        }`}
        data-testid="live-event-preview-video"
        onContextMenu={(event) => event.preventDefault()}
      />
      <audio
        ref={audioRef}
        autoPlay
        muted={false}
        playsInline
        data-testid="live-event-preview-audio"
      />

      {phase === "playing" && (
        <>
          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 text-xs font-black tracking-wide text-white backdrop-blur-md">
            <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
            LIVE PREVIEW
          </div>
          <div className="absolute right-3 top-3 rounded-full bg-black/70 px-3 py-1.5 font-mono text-xs font-bold text-white backdrop-blur-md">
            0:{String(secondsRemaining).padStart(2, "0")}
          </div>
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/95 via-black/55 to-transparent"
            data-testid="live-event-preview-bottom-gradient"
          />
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-black/70 px-3 py-1.5 text-[11px] font-semibold text-gray-200 backdrop-blur-md">
            <Volume2 className="h-3.5 w-3.5" /> Audio on · 10-second preview
          </div>
        </>
      )}

      {phase === "awaiting-gesture" && (
        <button
          type="button"
          onClick={() => playbackRef.current?.()}
          className="absolute inset-0 flex items-center justify-center bg-black/55 text-sm font-bold text-white backdrop-blur-sm"
          data-testid="live-event-preview-play"
        >
          Tap to play your 10-second preview with audio
        </button>
      )}

      {phase === "connecting" && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/65 px-3 py-1.5 text-[11px] font-semibold text-gray-300 backdrop-blur-md">
          Preparing live preview…
        </div>
      )}
    </div>
  );
};

export default LiveEventPreview;
